import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";

import moment from "moment";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import {
    downloadVLearnCertificate, getVLearnCollabDetail
} from "../../../containers/vlearn/vlearnSlice";
import {
    VLearnProgressRing, VLearnStatusChip, VLearnTypeIcon,
    formatVLearnDuration, isVLearnOverdue, vlearnType
} from "../vlearnUi";

/** Full-screen dialogs on phones slide up; the transition MUI's docs pair with them. */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

/**
 * One learner's whole picture on one course.
 *
 * Everything shown comes from `VLearnProgressController.getCollabDetail`, which returns
 * `{ collab, enrollment, lessons, attempts, certificate, activity }`. That endpoint refuses a
 * collaborator who is not enrolled on the course, so a dialog that opens with a stale id
 * shows the "could not be loaded" panel rather than another employee's training record.
 *
 * The lesson list is rendered in the server's order — section order, then lesson order —
 * because a teacher reads it against the curriculum they built, not against a ranking.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} props.idCourse
 * @param {number} props.idCollab
 * @param {() => void} props.onClose
 */
export default function CollabDetailDialog({ open, idCourse, idCollab, onClose }) {

    const dispatch = useDispatch()
    const theme = useTheme()
    // Full-screen below `sm`: the lesson list and the attempt list are both wide, and a
    // centred dialog on a phone leaves neither of them readable.
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const detail = useSelector((state) => state.vlearn.collabDetail)
    const lang = useSelector((state) => state.settings.lang)

    // Keyed on the pair actually fetched, so React 18's StrictMode double mount and any
    // re-render that does not change the target both resolve to a single request.
    const fetchedFor = useRef(null)

    useEffect(() => {
        if (!open || !idCourse || !idCollab) return
        const key = `${idCourse}:${idCollab}`
        if (fetchedFor.current === key) return
        fetchedFor.current = key
        dispatch(getVLearnCollabDetail({ id_course: idCourse, id_collab: idCollab }))
    }, [dispatch, open, idCourse, idCollab])

    // Cleared on close so re-opening the same learner refetches rather than showing figures
    // taken before they finished another lesson.
    useEffect(() => {
        if (!open) fetchedFor.current = null
    }, [open])

    const formatDate = useCallback((value, withTime) => {
        if (!value) return null
        const parsed = moment(value)
        if (!parsed.isValid()) return null
        // Instance-level locale: the global is set once in app/lang/langs.js and a component
        // that reassigns it changes every other screen's formatting too.
        return parsed.locale(lang || 'fr').format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
    }, [lang])

    // The response is only for the learner currently open. A previous learner's detail
    // sitting in the slice would otherwise render under this one's name for one frame.
    const isForThisCollab = Boolean(detail && detail.collab
        && Number(detail.collab.id) === Number(idCollab))

    const collab = isForThisCollab ? detail.collab : null
    const enrollment = isForThisCollab ? (detail.enrollment || null) : null
    const attempts = isForThisCollab ? (detail.attempts || []) : []
    const certificate = isForThisCollab ? (detail.certificate || null) : null

    const fullName = collab
        ? [collab.prenom, collab.nom].filter(Boolean).join(' ')
        : ''

    const overdue = enrollment
        ? isVLearnOverdue(enrollment.due_date, enrollment.status)
        : false

    /**
     * Lessons grouped under their section heading, preserving the server's ordering.
     *
     * The array is read out of `detail` inside the callback rather than through a derived
     * binding: a `detail.lessons || []` computed in the render body is a fresh array every
     * time, which would make this memo recompute on every render whether or not the data
     * changed. The ownership check is repeated here for the same reason it exists above — a
     * previous learner's lessons must not group under this one's name.
     */
    const sections = useMemo(() => {
        const source = (detail && detail.collab
            && Number(detail.collab.id) === Number(idCollab) && detail.lessons) || []
        const grouped = []
        for (const lesson of source) {
            const title = lesson.section_title || ''
            const last = grouped[grouped.length - 1]
            if (last && last.title === title) last.lessons.push(lesson)
            else grouped.push({ title, lessons: [lesson] })
        }
        return grouped
    }, [detail, idCollab])

    const onDownloadCertificate = useCallback(() => {
        if (!certificate) return
        // The serial is already ASCII-safe and unique, which makes it the right thing to name
        // the file after: two certificates downloaded into the same folder cannot collide.
        dispatch(downloadVLearnCertificate({
            idCourse,
            idCollab,
            filename: `Attestation_${certificate.serial}.pdf`
        }))
    }, [dispatch, idCourse, idCollab, certificate])

    const photoUrl = collab
        ? NetworkManager.getInstance().getCollabPhotoUrl(collab.id, collab.photo_updated_at)
        : null

    return (
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            TransitionComponent={fullScreen ? SlideUp : undefined}
            aria-labelledby="vlearn-collab-detail-title"
        >
            <DialogTitle
                id="vlearn-collab-detail-title"
                component="h2"
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.07)', py: 2
                }}
            >
                <Avatar
                    src={photoUrl || undefined}
                    sx={{
                        width: 44, height: 44, fontSize: 15,
                        bgcolor: 'var(--lightGrey, #ededed)', color: '#6f6f6f'
                    }}>
                    {[collab && collab.prenom, collab && collab.nom].filter(Boolean)
                        .map((part) => String(part).charAt(0).toUpperCase()).join('')}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...vlearnType.sectionTitle, fontSize: '1.05rem' }}>
                        {fullName || <Langs str="VLEARN_COLLAB_DETAIL_TITLE" />}
                    </Typography>
                    {/*
                      * The email, not the team name. `getCollabDetail` returns the enrolment
                      * row — which carries `id_team` but no team name — and inventing a
                      * lookup for it here would be a second source for a fact the learner
                      * table already shows correctly. The email identifies the person
                      * unambiguously, which is what this line is for.
                      */}
                    <Typography sx={{ ...vlearnType.meta }}>
                        {collab ? collab.email : null}
                    </Typography>
                    {enrollment ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                            <VLearnStatusChip status={enrollment.status} overdue={overdue} />
                            <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                <Langs
                                    str="VLEARN_DETAIL_LESSONS_DONE"
                                    params={{
                                        done: Number(enrollment.lessons_completed) || 0,
                                        total: Number(enrollment.lessons_total) || 0
                                    }}
                                />
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                {enrollment ? (
                    <VLearnProgressRing
                        percent={Number(enrollment.progress_percent) || 0}
                        status={overdue ? 'overdue' : enrollment.status}
                        size={52}
                    />
                ) : null}

                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label={Localize("VLEARN_CLOSE", lang)}
                    sx={{ ml: 0.5 }}
                >
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 2.5 }}>

                {!isForThisCollab ? (
                    <Typography sx={{ ...vlearnType.body }}>
                        <Langs str="VLEARN_DETAIL_NOT_LOADED" />
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* ── Dates, as a plain definition row ─────────────────────── */}
                        {enrollment ? (
                            <Box sx={{
                                display: 'flex', gap: 2.5, flexWrap: 'wrap',
                                fontVariantNumeric: 'tabular-nums'
                            }}>
                                {formatDate(enrollment.started_at, false) ? (
                                    <Typography sx={{ ...vlearnType.meta }}>
                                        <Langs str="VLEARN_DETAIL_STARTED_ON"
                                            params={{ date: formatDate(enrollment.started_at, false) }} />
                                    </Typography>
                                ) : null}
                                {formatDate(enrollment.last_accessed_at, true) ? (
                                    <Typography sx={{ ...vlearnType.meta }}>
                                        <Langs str="VLEARN_DETAIL_LAST_ACCESS"
                                            params={{ date: formatDate(enrollment.last_accessed_at, true) }} />
                                    </Typography>
                                ) : null}
                                {formatDate(enrollment.completed_at, false) ? (
                                    <Typography sx={{ ...vlearnType.meta }}>
                                        <Langs str="VLEARN_DETAIL_COMPLETED_ON"
                                            params={{ date: formatDate(enrollment.completed_at, false) }} />
                                    </Typography>
                                ) : null}
                                <Typography sx={{
                                    ...vlearnType.meta,
                                    color: overdue ? '#B3271F' : undefined,
                                    fontWeight: overdue ? 700 : undefined
                                }}>
                                    {formatDate(enrollment.due_date, false)
                                        ? <Langs str="VLEARN_DETAIL_DUE_ON"
                                            params={{ date: formatDate(enrollment.due_date, false) }} />
                                        : <Langs str="VLEARN_DETAIL_NO_DUE_DATE" />}
                                </Typography>
                            </Box>
                        ) : null}

                        {/* ── Lesson by lesson ─────────────────────────────────────── */}
                        <Box component="section">
                            <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 1.25 }}>
                                <Langs str="VLEARN_DETAIL_LESSONS" />
                            </Typography>

                            {sections.map((section, sectionIndex) => (
                                <Box key={`${section.title}-${sectionIndex}`} sx={{ mb: 2 }}>
                                    {section.title ? (
                                        <Typography sx={{
                                            ...vlearnType.meta, textTransform: 'uppercase',
                                            letterSpacing: '.04em', fontWeight: 700, mb: 0.75
                                        }}>
                                            {section.title}
                                        </Typography>
                                    ) : null}

                                    <Box sx={{
                                        border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px',
                                        overflow: 'hidden'
                                    }}>
                                        {section.lessons.map((lesson, lessonIndex) => {
                                            const duration = formatVLearnDuration(lesson.seconds_spent)
                                            const views = Number(lesson.views) || 0
                                            return (
                                                <Box
                                                    key={lesson.id}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                                        px: 1.5, py: 1.1,
                                                        borderTop: lessonIndex === 0
                                                            ? 'none' : '1px solid rgba(0,0,0,0.06)',
                                                        flexWrap: 'wrap'
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={Localize(`VLEARN_TYPE_${String(lesson.type || 'text').toUpperCase()}`, lang)}
                                                        arrow
                                                    >
                                                        <Box sx={{ display: 'flex' }}>
                                                            <VLearnTypeIcon type={lesson.type} />
                                                        </Box>
                                                    </Tooltip>

                                                    <Box sx={{ flex: 1, minWidth: 140 }}>
                                                        <Typography sx={{ ...vlearnType.cardTitle }}>
                                                            {lesson.title}
                                                            {Number(lesson.is_mandatory) ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={<Langs str="VLEARN_LESSON_MANDATORY" />}
                                                                    sx={{
                                                                        ml: 0.75, height: 17, fontSize: 10,
                                                                        backgroundColor: 'rgba(0,40,128,0.08)',
                                                                        color: 'var(--vital-blue, #002880)'
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </Typography>
                                                        {formatDate(lesson.completed_at, false) ? (
                                                            <Typography sx={{ ...vlearnType.meta }}>
                                                                <Langs str="VLEARN_DETAIL_COMPLETED_ON"
                                                                    params={{ date: formatDate(lesson.completed_at, false) }} />
                                                            </Typography>
                                                        ) : null}
                                                    </Box>

                                                    <Box sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        <Tooltip title={Localize("VLEARN_DETAIL_TIME_SPENT", lang)} arrow>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                                <ScheduleRoundedIcon sx={{ fontSize: 14, color: '#9aa0ad' }} />
                                                                <Typography sx={{ ...vlearnType.meta }}>
                                                                    {duration || '—'}
                                                                </Typography>
                                                            </Box>
                                                        </Tooltip>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                            <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#9aa0ad' }} />
                                                            <Typography sx={{ ...vlearnType.meta }}>
                                                                <Langs
                                                                    str={views === 1
                                                                        ? "VLEARN_DETAIL_VIEWS_ONE"
                                                                        : "VLEARN_DETAIL_VIEWS_MANY"}
                                                                    params={{ count: views }}
                                                                />
                                                            </Typography>
                                                        </Box>
                                                        <VLearnStatusChip status={lesson.status} />
                                                    </Box>
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Divider />

                        {/* ── Quiz attempts ────────────────────────────────────────── */}
                        <Box component="section">
                            <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 1.25 }}>
                                <Langs str="VLEARN_DETAIL_QUIZZES" />
                            </Typography>

                            {attempts.length === 0 ? (
                                <Typography sx={{ ...vlearnType.body }}>
                                    <Langs str="VLEARN_DETAIL_NO_QUIZ_ATTEMPTS" />
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {attempts.map((attempt) => {
                                        /*
     * An `expired` attempt is GRADED, not in progress.
     *
     * The server grades a late submission and marks it `expired` rather than discarding the
     * learner's work. Testing only for `'submitted'` showed the teacher an hourglass, a dash
     * instead of the mark, and the label "in progress" for a paper that has a score.
     */
    const submitted = attempt.status === 'submitted' || attempt.status === 'expired'
                                        const passed = Boolean(Number(attempt.passed))
                                        return (
                                            <Box
                                                key={attempt.id}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.25,
                                                    border: '1px solid rgba(0,0,0,0.08)',
                                                    borderRadius: '10px', px: 1.5, py: 1.1, flexWrap: 'wrap'
                                                }}
                                            >
                                                {/* Icon plus a worded chip: the outcome never rests
                                                    on the colour of a dot alone. */}
                                                {!submitted
                                                    ? <HourglassTopRoundedIcon sx={{ fontSize: 18, color: 'var(--orange, rgb(255,144,21))' }} />
                                                    : passed
                                                        ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'var(--green, rgb(58,156,96))' }} />
                                                        : <CancelRoundedIcon sx={{ fontSize: 18, color: '#B3271F' }} />}

                                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                                    <Typography sx={{ ...vlearnType.cardTitle }}>
                                                        {attempt.quiz_title}
                                                    </Typography>
                                                    <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                                        <Langs str="VLEARN_ATTEMPT_NUMBER"
                                                            params={{ number: attempt.attempt_no }} />
                                                        {' · '}
                                                        <Langs str="VLEARN_PASSING_THRESHOLD"
                                                            params={{ percent: Number(attempt.passing_percent) || 0 }} />
                                                        {formatDate(attempt.submitted_at || attempt.started_at, true)
                                                            ? ` · ${formatDate(attempt.submitted_at || attempt.started_at, true)}`
                                                            : ''}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{
                                                    textAlign: 'right', fontVariantNumeric: 'tabular-nums'
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '1rem', fontWeight: 700,
                                                        color: 'var(--text-primary, #111318)'
                                                    }}>
                                                        {submitted
                                                            ? `${(typeof attempt.percent === 'number' || (attempt.percent !== null
            && attempt.percent !== undefined && Number.isFinite(Number(attempt.percent)))
            // A submitted attempt can arrive with no `percent` — `show_score = 0` makes the
            // server omit it deliberately. Formatting it unguarded rendered literally "NaN%".
            // LearnerProgressTable guards this same field; this file did not.
            ? Math.round(Number(attempt.percent) * 10) / 10
            : null)}%`
                                                            : '—'}
                                                    </Typography>
                                                    <Typography sx={{ ...vlearnType.meta }}>
                                                        {submitted
                                                            ? `${Number(attempt.score_points) || 0} / ${Number(attempt.max_points) || 0}`
                                                            : <Langs str="VLEARN_ATTEMPT_IN_PROGRESS" />}
                                                    </Typography>
                                                </Box>

                                                {submitted ? (
                                                    <Chip
                                                        size="small"
                                                        label={<Langs str={passed
                                                            ? "VLEARN_ATTEMPT_PASSED" : "VLEARN_ATTEMPT_FAILED"} />}
                                                        sx={{
                                                            fontWeight: 700, fontSize: '0.72rem', borderRadius: '7px',
                                                            backgroundColor: passed
                                                                ? 'rgba(58,156,96,0.14)' : 'rgba(251,105,98,0.16)',
                                                            color: passed ? '#1F7A4C' : '#B3271F'
                                                        }}
                                                    />
                                                ) : null}
                                            </Box>
                                        )
                                    })}
                                </Box>
                            )}
                        </Box>

                        <Divider />

                        {/* ── Certificate ──────────────────────────────────────────── */}
                        <Box component="section">
                            <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 1.25 }}>
                                <Langs str="VLEARN_DETAIL_CERTIFICATE" />
                            </Typography>

                            {!certificate ? (
                                <Typography sx={{ ...vlearnType.body }}>
                                    <Langs str="VLEARN_DETAIL_NO_CERTIFICATE" />
                                </Typography>
                            ) : (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                                    border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px',
                                    px: 1.75, py: 1.5
                                }}>
                                    <WorkspacePremiumOutlinedIcon
                                        sx={{ fontSize: 28, color: 'var(--green, rgb(58,156,96))' }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 180 }}>
                                        <Typography sx={{ ...vlearnType.cardTitle }}>
                                            {certificate.course_title}
                                        </Typography>
                                        <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                                            <Langs str="VLEARN_CERT_SERIAL"
                                                params={{ serial: certificate.serial }} />
                                            {formatDate(certificate.issued_at, false)
                                                ? ' · ' : ''}
                                            {formatDate(certificate.issued_at, false)
                                                ? <Langs str="VLEARN_CERT_ISSUED_ON"
                                                    params={{ date: formatDate(certificate.issued_at, false) }} />
                                                : null}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownloadRoundedIcon />}
                                        onClick={onDownloadCertificate}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        <Langs str="VLEARN_DOWNLOAD_CERTIFICATE" />
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    )
}
